import Papa from "papaparse";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { adminReqConfig, api } from "../../include/api";
import "../../styles/Super.css";
import AdminHeader from "./Header";
import { ChevronDown,X } from "react-feather";
import { useNotification } from "../../Toast/ToastProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ERROR, SUCCESS } from "../../include/constant";
import { getAdmin } from "../../include/function";

const ListDeliveryCharges = () => {
  const navigate = useNavigate();
  const [fetchLoading, setFetchLoading] = useState(true);
  const [orderData, setOrderData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const toast = useNotification();
  const [selectedData, setSelectedData] = useState([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [zones, setZones] = useState([]);
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(initialQuery);

  useEffect(() => {
    const q = searchParams.get("q") || "";
    setSearchQuery(q);
  }, [searchParams]);

  const [filterValues, setFilterValues] = useState({
    zone: "",
    productId: "",
    brandId: "",
    categoryId: "",
  });

  const user = getAdmin();
  const [productPermissions, setProductPermissions] = useState({
    add: false,
    view: false,
    edit: false,
    delete: false,
  });

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.roleId) return;
      try {
        const res = await api.get(
          `/role_permission/?roleId=${user.roleId}`,
          adminReqConfig(),
        );
        if (res.status === 200 && Array.isArray(res.data.data)) {
          const productPerm = res.data.data.find(
            (p) => p.pageTitle === "Delivery Charge",
          );
          if (productPerm) {
            setProductPermissions({
              add: productPerm.add === "true" || productPerm.add === 1,
              view: productPerm.view === "true" || productPerm.view === 1,
              edit: productPerm.edit === "true" || productPerm.edit === 1,
              delete: productPerm.delete === "true" || productPerm.delete === 1,
            });
          }
        }
      } catch (err) {
        console.warn("Failed to fetch product permissions", err);
      }
    };
    fetchPermissions();
  }, [user?.roleId]);

  // Fetch lookup data
  useEffect(() => {
    api
      .get("/brand/", adminReqConfig())
      .then((res) => setBrands(res.data.data || []))
      .catch(() => setBrands([]));
    api
      .get("/category/?flag=exceptHome", adminReqConfig())
      .then((res) => setCategories(res.data.data || []))
      .catch(() => setCategories([]));
    api
      .get("/product/?limit=200", adminReqConfig())
      .then((res) => setProducts(res.data.data || []))
      .catch(() => setProducts([]));
    api
      .get("/zone/", adminReqConfig())
      .then((res) => setZones(res.data.data || []))
      .catch(() => setZones([]));
  }, []);

  // Fetch delivery charges
  const getOrder = () => {
    setFetchLoading(true);
    api
      .get("/delivery_charges/", adminReqConfig())
      .then(async (response) => {
        if (response.status === 200 && Array.isArray(response.data.data)) {
          const allZoneIds = new Set();
          const allProductIds = new Set();
          const allBrandIds = new Set();
          const allCategoryIds = new Set();

          response.data.data.forEach((charge) => {
            if (Array.isArray(charge.zoneId)) {
              charge.zoneId.forEach((id) => {
                const n = parseInt(id, 10);
                if (!Number.isNaN(n)) allZoneIds.add(n);
              });
            } else if (charge.zoneId != null && charge.zoneId !== "") {
              const n = parseInt(charge.zoneId, 10);
              if (!Number.isNaN(n)) allZoneIds.add(n);
            }

            if (Array.isArray(charge.productList))
              charge.productList.forEach((id) => allProductIds.add(id));
            if (Array.isArray(charge.brandList))
              charge.brandList.forEach((id) => allBrandIds.add(id));
            if (Array.isArray(charge.categoryList))
              charge.categoryList.forEach((id) => allCategoryIds.add(id));
          });

          const [
            fetchedZones,
            fetchedProducts,
            fetchedBrands,
            fetchedCategories,
          ] = await Promise.all([
            allZoneIds.size > 0
              ? api
                  .get(
                    `/zone/?ids=${Array.from(allZoneIds).join(",")}`,
                    adminReqConfig(),
                  )
                  .then((r) => r.data.data || [])
              : [],
            allProductIds.size > 0
              ? api
                  .get(
                    `/product/?ids=${Array.from(allProductIds).join(",")}`,
                    adminReqConfig(),
                  )
                  .then((r) => r.data.data || [])
              : [],
            allBrandIds.size > 0
              ? api
                  .get(
                    `/brand/?ids=${Array.from(allBrandIds).join(",")}`,
                    adminReqConfig(),
                  )
                  .then((r) => r.data.data || [])
              : [],
            allCategoryIds.size > 0
              ? api
                  .get(
                    `/category/?ids=${Array.from(allCategoryIds).join(",")}`,
                    adminReqConfig(),
                  )
                  .then((r) => r.data.data || [])
              : [],
          ]);

          const zoneMap = new Map(
            fetchedZones.map((z) => [Number(z.id), z.name]),
          );
          const productMap = new Map(
            fetchedProducts.map((p) => [Number(p.id), p.name]),
          );
          const brandMap = new Map(
            fetchedBrands.map((b) => [Number(b.id), b.name]),
          );
          const categoryMap = new Map(
            fetchedCategories.map((c) => [Number(c.id), c.name]),
          );

          const normalizedData = response.data.data.map((charge, index) => {
            const zoneIds = Array.isArray(charge.zoneId)
              ? charge.zoneId
                  .map((id) => parseInt(id, 10))
                  .filter((id) => !Number.isNaN(id))
              : (() => {
                  const n = parseInt(charge.zoneId, 10);
                  return Number.isNaN(n) ? [] : [n];
                })();

            const productIds = Array.isArray(charge.productList)
              ? charge.productList.map((id) => parseInt(id))
              : [];

            const brandIds = Array.isArray(charge.brandList)
              ? charge.brandList.map((id) => parseInt(id))
              : [];

            const categoryIds = Array.isArray(charge.categoryList)
              ? charge.categoryList.map((id) => parseInt(id))
              : [];

            const zoneNames = zoneIds
              .map((id) => zoneMap.get(id) || ``)
              .filter(Boolean);

            const productNames = productIds
              .map((id) => productMap.get(id) || ``)
              .filter(Boolean);

            const brandNames = brandIds
              .map((id) => brandMap.get(id) || ``)
              .filter(Boolean);

            const categoryNames = categoryIds
              .map((id) => categoryMap.get(id) || ``)
              .filter(Boolean);

            return {
              id: charge.id || index,
              zoneId: zoneIds,
              zoneName: zoneNames.length > 0 ? zoneNames : ["-"],
              productId: productIds,
              productName: productNames.length > 0 ? productNames : ["-"],
              brandId: brandIds,
              brandName: brandNames.length > 0 ? brandNames : ["-"],
              categoryId: categoryIds,
              categoryName: categoryNames.length > 0 ? categoryNames : ["-"],
              minValue: charge.minQty || 0,
              maxValue: charge.maxQty || 0,
              minRate: charge.minRate || 0,
              maxRate: charge.maxRate || 0,
              unit: charge.unit || "₹",
              name: zoneNames.length > 0 ? zoneNames[0] : "-",
              customerId: charge.customerId || 0,
              phoneNumber: charge.phoneNumber || "-",
              address: charge.address || "-",
              locality: charge.locality || "-",
              city: charge.city || "-",
              state: charge.state || "-",
              totalAmount: charge.maxRate || 0,
              date: charge.date || new Date().toISOString(),
              basedOn: charge.basedOn || "distance",
            };
          });

          setOrderData(normalizedData);
          setFilteredData(normalizedData);
        } else {
          setOrderData([]);
          setFilteredData([]);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch delivery charges:", error);
        setOrderData([]);
        setFilteredData([]);
      })
      .finally(() => setFetchLoading(false));
  };

  useEffect(() => {
    getOrder();
  }, []);

  // 🔥 Build cascading filter options from orderData
  const { filteredCategories, filteredBrands, filteredProducts } =
    useMemo(() => {
      const allCategoryIdsInData = new Set();
      const allBrandIdsInData = new Set();
      const allProductIdsInData = new Set();
      const categoryToBrands = {};
      const brandToProducts = {};

      orderData.forEach((item) => {
        const categoryIds = Array.isArray(item.categoryId)
          ? item.categoryId
          : [item.categoryId];
        const brandIds = Array.isArray(item.brandId)
          ? item.brandId
          : [item.brandId];
        const productIds = Array.isArray(item.productId)
          ? item.productId
          : [item.productId];

        categoryIds.filter(Boolean).forEach((catId) => {
          allCategoryIdsInData.add(catId);
          if (!categoryToBrands[catId]) categoryToBrands[catId] = new Set();
          brandIds.filter(Boolean).forEach((brandId) => {
            allBrandIdsInData.add(brandId);
            categoryToBrands[catId].add(brandId);
          });
        });

        brandIds.filter(Boolean).forEach((brandId) => {
          if (!brandToProducts[brandId]) brandToProducts[brandId] = new Set();
          productIds.filter(Boolean).forEach((productId) => {
            allProductIdsInData.add(productId);
            brandToProducts[brandId].add(productId);
          });
        });
      });

      const filteredCategories = categories.filter((cat) =>
        allCategoryIdsInData.has(cat.id),
      );

      let filteredBrands = brands;
      if (filterValues.categoryId) {
        const catId = parseInt(filterValues.categoryId);
        filteredBrands = brands.filter(
          (brand) =>
            allBrandIdsInData.has(brand.id) &&
            categoryToBrands[catId]?.has(brand.id),
        );
      } else {
        filteredBrands = brands.filter((brand) =>
          allBrandIdsInData.has(brand.id),
        );
      }

      let filteredProducts = products;
      if (filterValues.brandId) {
        const brandId = parseInt(filterValues.brandId);
        filteredProducts = products.filter(
          (product) =>
            allProductIdsInData.has(product.id) &&
            brandToProducts[brandId]?.has(product.id),
        );
      } else if (filterValues.categoryId) {
        const catId = parseInt(filterValues.categoryId);
        filteredProducts = products.filter(
          (product) =>
            allProductIdsInData.has(product.id) &&
            orderData.some(
              (item) =>
                (Array.isArray(item.categoryId)
                  ? item.categoryId
                  : [item.categoryId]
                ).includes(catId) &&
                (Array.isArray(item.productId)
                  ? item.productId
                  : [item.productId]
                ).includes(product.id),
            ),
        );
      } else {
        filteredProducts = products.filter((product) =>
          allProductIdsInData.has(product.id),
        );
      }

      return { filteredCategories, filteredBrands, filteredProducts };
    }, [orderData, filterValues, brands, categories, products]);

  // 🔥 Filter on `orderData`
  useEffect(() => {
    let filtered = [...orderData];

    if (filterValues.zone) {
      const zoneId = parseInt(filterValues.zone);
      filtered = filtered.filter((item) =>
        Array.isArray(item.zoneId)
          ? item.zoneId.includes(zoneId)
          : item.zoneId === zoneId,
      );
    }
    if (filterValues.categoryId) {
      const catId = parseInt(filterValues.categoryId);
      filtered = filtered.filter((item) =>
        Array.isArray(item.categoryId)
          ? item.categoryId.includes(catId)
          : item.categoryId === catId,
      );
    }
    if (filterValues.brandId) {
      const brandId = parseInt(filterValues.brandId);
      filtered = filtered.filter((item) =>
        Array.isArray(item.brandId)
          ? item.brandId.includes(brandId)
          : item.brandId === brandId,
      );
    }
    if (filterValues.productId) {
      const prodId = parseInt(filterValues.productId);
      filtered = filtered.filter((item) =>
        Array.isArray(item.productId)
          ? item.productId.includes(prodId)
          : item.productId === prodId,
      );
    }
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      const resolveZoneNames = (item) => {
        const ids = Array.isArray(item.zoneId) ? item.zoneId : [item.zoneId];
        const fromLookup = ids
          .map((id) => zones.find((z) => String(z.id) === String(id))?.name)
          .filter(Boolean);
        if (fromLookup.length > 0) return fromLookup;
        return (item.zoneName || []).filter((name) => name && name !== "-");
      };
      filtered = filtered.filter(
        (item) =>
          resolveZoneNames(item).some((name) =>
            name.toLowerCase().includes(query),
          ) ||
          item.productName.some(
            (name) =>
              name !== "-" && name.toLowerCase().includes(query),
          ) ||
          item.categoryName.some(
            (name) =>
              name !== "-" && name.toLowerCase().includes(query),
          ) ||
          item.brandName.some(
            (name) => name !== "-" && name.toLowerCase().includes(query),
          ),
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [filterValues, searchQuery, orderData, searchParams, zones]);

  // Pagination logic
  let currentItems = [];
  let totalPage = 1;
  let showingFrom = 0;
  let showingTo = 0;
  if (itemsPerPage === "all") {
    currentItems = filteredData;
    showingFrom = filteredData.length > 0 ? 1 : 0;
    showingTo = filteredData.length;
  } else {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    totalPage = Math.ceil(filteredData.length / itemsPerPage);
    showingFrom = indexOfFirstItem + 1;
    showingTo = Math.min(indexOfLastItem, filteredData.length);
  }
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Outside click handler
  const filterWrapperRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isFilterPanelOpen &&
        filterWrapperRef.current &&
        !filterWrapperRef.current.contains(event.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isFilterPanelOpen]);

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = currentItems.map((p) => p.id);
      setSelectedData((prev) => [...new Set([...prev, ...allIds])]);
    } else {
      const idsToRemove = currentItems.map((p) => p.id);
      setSelectedData((prev) => prev.filter((id) => !idsToRemove.includes(id)));
    }
  };
  const handleSelectOne = (id) => {
    setSelectedData((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id],
    );
  };

  // Filter handlers
  const applyFilters = () => setIsFilterPanelOpen(false);
  const clearFilters = () => {
    setFilterValues({ zone: "", productId: "", brandId: "", categoryId: "" });
    setIsFilterPanelOpen(false);
  };
  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === "categoryId") {
        updated.brandId = "";
        updated.productId = "";
      } else if (key === "brandId") {
        updated.productId = "";
      }
      return updated;
    });
  };

  // Actions
  const handleEdit = (id) => {
    navigate(`/admin/deliveryCharge/deliveryChargeAddNew/${id}`);
  };
  const handleDelete = (id) => {
    if (
      window.confirm("Are you sure you want to delete this delivery charge?")
    ) {
      api
        .delete(`/delivery_charges/?id=${id}`, adminReqConfig())
        .then((response) => {
          toast({
            type: SUCCESS,
            message:
              response.data.message || "Delivery charge deleted successfully",
          });
          setOrderData((prev) => prev.filter((p) => p.id !== id));
          setFilteredData((prev) => prev.filter((p) => p.id !== id));
        })
        .catch((error) => {
          toast({
            type: ERROR,
            message:
              error.response?.data?.message ||
              "Failed to delete delivery charge",
          });
        });
    }
  };

  // CSV Export
  const handleDownloadCSV = (exportScope = "current") => {
    let dataToExport = [];

    // ✅ PRIORITY 1: If items selected → ALWAYS export selected (overrides everything)
    if (selectedData.length > 0) {
      dataToExport = orderData.filter((order) =>
        selectedData.includes(order.id),
      );
    }
    // ✅ PRIORITY 2: If "Export All" clicked → export entire dataset
    else if (exportScope === "all") {
      dataToExport = orderData;
    }
    // ✅ PRIORITY 3: Default → export current page items only
    else {
      dataToExport = currentItems;
    }

    if (dataToExport.length === 0) {
      toast({ type: ERROR, message: "No delivery charges data to download" });
      return;
    }

    const csvRows = [];
    let serialNo = 1;
    dataToExport.forEach((order) => {
      const baseOrderInfo = {
        "S.No": serialNo++,
        Zone: (Array.isArray(order.zoneId) ? order.zoneId : [order.zoneId])
          .map((id) => zones.find((z) => String(z.id) === String(id))?.name)
          .filter(Boolean)
          .join(", "),
        Product: Array.isArray(order.productName)
          ? order.productName.join(", ")
          : order.productName,
        Category: Array.isArray(order.categoryName)
          ? order.categoryName.join(", ")
          : order.categoryName,
        Brand: Array.isArray(order.brandName)
          ? order.brandName.join(", ")
          : order.brandName,
      };
      csvRows.push(baseOrderInfo);
    });

    const columns = ["S.No", "Zone", "Product", "Category", "Brand"];

    // ✅ FIXED: Added "data:" key before the mapping array
    const csv = Papa.unparse({
      fields: columns,
      data: csvRows.map((row) => columns.map((col) => row[col])), // ← "data:" was missing!
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // ✅ Dynamic filename based on export type
    const fileName =
      selectedData.length > 0
        ? `DeliveryCharge_Selected_${selectedData.length}.csv`
        : exportScope === "all"
          ? "DeliveryCharge_All.csv"
          : `DeliveryCharge_Page${currentPage}.csv`;

    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <AdminHeader title={"DELIVERY CHARGES LIST"}>
        <div className="content-wrapper p-0">
          <section
            style={{
              backgroundColor: "#F1F4F9",
              padding: "30px",
              marginTop: "70px",
              marginBottom: "40px",
              minHeight: "80vh",
            }}
          >
            {fetchLoading ? (
              <p>Loading delivery charges...</p>
            ) : (
              <>
                <div className="rounded-5 bg-white py-24">
                  <div className="row align-items-center px-24 py-10 mb-3">
                    {/* Header */}
                    <div className="col-auto">
                      <h6 className="mb-0">All Delivery List</h6>
                    </div>
                    {/* Search + Filter */}
                    <div className="col d-flex align-items-center">
                      <div
                        className="filter-wrapper"
                        style={{
                          position: "relative",
                          width: "100%",
                          maxWidth: "500px",
                        }}
                        ref={filterWrapperRef}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #c6d6f5",
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            width: "100%",
                          }}
                        >
                          <img
                            src="/assets/images/search-icon.png"
                            style={{ width: "18px", marginLeft: "8px" }}
                          />
                          <input
                            type="text"
                            placeholder="Filter and search here..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => setIsFilterPanelOpen(true)}
                            style={{
                              border: "none",
                              outline: "none",
                              padding: "8px 8px",
                              width: "100%",
                              fontSize: "14px",
                              color: "#333",
                              backgroundColor: "transparent",
                            }}
                            />
                             {searchQuery && (
                                                      <X
                                                        size={24}
                                                        onClick={(e) => {
                                                          e.stopPropagation(); 
                                                          setSearchQuery(""); 
                                                          setCurrentPage(1);
                                                          setIsFilterPanelOpen(false);
                                                        }}
                                                        style={{
                                                          position: "absolute",
                                                          right: "40px", 
                                                          cursor: "pointer",
                                                          color: "#999",
                                                          padding: "4px",
                                                          zIndex: 10,
                                                        }}
                                                        onMouseEnter={(e) =>
                                                          (e.currentTarget.style.color = "#666")
                                                        }
                                                        onMouseLeave={(e) =>
                                                          (e.currentTarget.style.color = "#999")
                                                        }
                                                      />
                                                    )}
                          <ChevronDown
                            size={16}
                            style={{
                              marginRight: "8px",
                              color: "#999",
                              cursor: "pointer",
                              transform: isFilterPanelOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsFilterPanelOpen(!isFilterPanelOpen);
                            }}
                          />
                        </div>
                        {isFilterPanelOpen && (
                          <FilterPanel
                            filterValues={filterValues}
                            onFilterChange={handleFilterChange}
                            onApply={applyFilters}
                            onClear={clearFilters}
                            brands={filteredBrands}
                            categories={filteredCategories}
                            products={filteredProducts}
                            zones={zones}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>
                    </div>
                    {/* Add New + Options */}
                    <div className="col-auto d-flex align-items-center gap-2">
                      {productPermissions.add && (
                        <button
                          className="btn me-0 mb-0"
                          style={{
                            height: "40px",
                            width: "140px",
                            color: "white",
                            backgroundColor: "#2696D5",
                            border: "none",
                            borderRadius: "6px",
                          }}
                          onClick={() =>
                            navigate(
                              "/admin/deliveryCharge/deliveryChargeAddNew",
                            )
                          }
                        >
                          Add New
                        </button>
                      )}
                      <CustomDropdown handleDownloadCSV={handleDownloadCSV} />
                    </div>
                  </div>

                  {/* Table */}
                  <div className="table-responsive mt-24 product-table">
                    <table className="table table-striped custom-striped-table-product-list align-middle">
                      <thead>
                        <tr>
                          <th scope="col">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{ borderColor: "#c5d5f5" }}
                              onChange={handleSelectAll}
                              checked={
                                currentItems.length > 0 &&
                                currentItems.every((p) =>
                                  selectedData.includes(p.id),
                                )
                              }
                            />
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            S.No
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Zone
                          </th>
                          {/* <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Value (Min-Max)
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Charges
                          </th> */}
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Product
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Category
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Brand
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", width: "120px" }}
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? (
                          currentItems.map((order, index) => {
                            const serialNumber =
                              (currentPage - 1) * itemsPerPage + index + 1;
                            return (
                              <tr key={order.id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    style={{ borderColor: "#c5d5f5" }}
                                    className="form-check-input"
                                    checked={selectedData.includes(order.id)}
                                    onChange={() => handleSelectOne(order.id)}
                                  />
                                </td>
                                <td>
                                  <span>
                                    {String(serialNumber).padStart(2, "0")}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-capitalize">
                                    {(Array.isArray(order.zoneId)
                                      ? order.zoneId
                                      : [order.zoneId]
                                    )
                                      .map(
                                        (id) =>
                                          zones.find(
                                            (z) => String(z.id) === String(id),
                                          )?.name,
                                      )
                                      .filter(Boolean)
                                      .join(", ")}
                                  </span>
                                </td>
                                {/* <td>
                                  {`${order.minValue} - ${order.maxValue} ${
                                    order.basedOn === "distance"
                                      ? "kms"
                                      : order.basedOn === "weight"
                                        ? "kgs"
                                        : order.basedOn === "quantity"
                                          ? "Bags"
                                          : order.basedOn || "₹"
                                  }`}
                                </td>
                                <td>{`₹${order.minRate}`}</td> */}
                                <td>
                                  {order.productName.length > 1
                                    ? `${order.productName[0]} +${order.productName.length - 1}`
                                    : order.productName[0]}
                                </td>
                                <td>
                                  {order.categoryName.length > 1
                                    ? `${order.categoryName[0]} +${order.categoryName.length - 1}`
                                    : order.categoryName[0]}
                                </td>
                                <td>
                                  {order.brandName.length > 1
                                    ? `${order.brandName[0]} +${order.brandName.length - 1}`
                                    : order.brandName[0]}
                                </td>
                                <td style={{ position: "relative" }}>
                                  <div className="d-flex gap-1">
                                    {productPermissions.edit && (
                                      <button
                                        style={{
                                          backgroundColor: "#d9ebff",
                                          height: "39px",
                                          width: "50px",
                                          border: "none",
                                          borderRadius: "4px",
                                          display: "flex",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          cursor: "pointer",
                                          padding: "0",
                                          margin: "0",
                                          fontSize: "14px",
                                          lineHeight: 1,
                                          color: "inherit",
                                          outline: "none",
                                        }}
                                        onClick={() => handleEdit(order.id)}
                                      >
                                        <img
                                          src="/assets/images/edit-icon.png"
                                          alt="Edit"
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            objectFit: "contain",
                                          }}
                                          onError={(e) =>
                                            (e.target.style.display = "none")
                                          }
                                        />
                                      </button>
                                    )}
                                    {productPermissions.delete && (
                                      <button
                                        style={{
                                          backgroundColor: "#d9ebff",
                                          height: "39px",
                                          width: "50px",
                                          border: "none",
                                          borderRadius: "4px",
                                          display: "flex",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          cursor: "pointer",
                                          padding: "0",
                                          margin: "0",
                                          fontSize: "14px",
                                          lineHeight: 1,
                                          color: "inherit",
                                          outline: "none",
                                        }}
                                        onClick={() => handleDelete(order.id)}
                                      >
                                        <img
                                          src="/assets/images/delete-icon.png"
                                          alt="Delete"
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            objectFit: "contain",
                                          }}
                                          onError={(e) =>
                                            (e.target.style.display = "none")
                                          }
                                        />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-3">
                              No Delivery Charges found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center mt-16 p-16">
                    <div className="d-flex align-items-center">
                      <select
                        className="form-select d-inline-block w-auto"
                        value={itemsPerPage}
                        onChange={(e) => {
                          const val = e.target.value;
                          setItemsPerPage(
                            val === "all" ? "all" : parseInt(val),
                          );
                          setCurrentPage(1);
                        }}
                        style={{
                          height: "34px",
                          fontSize: "14px",
                          minWidth: "70px",
                        }}
                      >
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="all">All</option>
                      </select>
                      <span className="ms-2">
                        Showing {showingFrom} to {showingTo} of{" "}
                        {filteredData.length} Lists
                      </span>
                    </div>
                    <nav>
                      <ul className="pagination mt-0">
                        <li
                          className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage - 1)}
                          >
                            &laquo;
                          </button>
                        </li>
                        {(() => {
                          const pagesToShow = 5;
                          let startPage = Math.max(
                            1,
                            currentPage - Math.floor(pagesToShow / 2),
                          );
                          let endPage = startPage + pagesToShow - 1;
                          if (endPage > totalPage) {
                            endPage = totalPage;
                            startPage = Math.max(1, endPage - pagesToShow + 1);
                          }
                          const pageNumbers = [];
                          for (let i = startPage; i <= endPage; i++) {
                            pageNumbers.push(i);
                          }
                          return pageNumbers.map((pageNum) => (
                            <li
                              key={pageNum}
                              className={`page-item ${currentPage === pageNum ? "active" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => paginate(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          ));
                        })()}
                        <li
                          className={`page-item ${currentPage === totalPage ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage + 1)}
                          >
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </AdminHeader>
    </>
  );
};

export default ListDeliveryCharges;

// --- Filter Panel Component ---
const FilterPanel = ({
  filterValues,
  onFilterChange,
  onApply,
  onClear,
  brands,
  categories,
  products,
  zones,
  onClick,
}) => {
  return (
    <div
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        width: "500px",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        zIndex: 1000,
        padding: "16px",
        maxHeight: "500px",
        overflowY: "auto",
        marginTop: "10px",
      }}
      onClick={onClick}
    >
      <div className="row g-3">
        <div className="col-md-6">
          <select
            className="form-select filter-input"
            value={filterValues.zone}
            onChange={(e) => onFilterChange("zone", e.target.value)}
          >
            <option value="">Zone</option>
            {zones.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <select
            className="form-select filter-input"
            value={filterValues.categoryId}
            onChange={(e) => onFilterChange("categoryId", e.target.value)}
          >
            <option value="">Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <select
            className="form-select filter-input"
            value={filterValues.brandId}
            onChange={(e) => onFilterChange("brandId", e.target.value)}
          >
            <option value="">Brand</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <select
            className="form-select filter-input"
            value={filterValues.productId}
            onChange={(e) => onFilterChange("productId", e.target.value)}
          >
            <option value="">Product</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
                {product.brandName ? ` (${product.brandName})` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="d-flex gap-14 mt-24">
        <button
          onClick={onApply}
          style={{
            color: "#fff",
            backgroundColor: "#2b95d3",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            padding: "8px 16px",
            minWidth: "150px",
            height: "40px",
            border: "none",
          }}
        >
          Apply Filters
        </button>
        <button
          onClick={onClear}
          style={{
            color: "#2692d2",
            backgroundColor: "#d9ebff",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            padding: "8px 16px",
            minWidth: "150px",
            height: "40px",
            border: "none",
            gap: "6px",
          }}
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

const CustomDropdown = ({ handleDownloadCSV }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState("Options");
  const dropdownRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const options = [
    { label: "Export", value: "export" },
    { label: "Export All", value: "exportAll" },
  ];
  const handleSelect = (opt) => {
    setSelected(opt.label);
    setIsOpen(false);

    if (opt.value === "export") {
      // ✅ Export = current page only (but selected overrides)
      handleDownloadCSV("current");
    } else if (opt.value === "exportAll") {
      // ✅ Export All = entire dataset (but selected overrides)
      handleDownloadCSV("all");
    }

    setSelected("Options");
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);
  return (
    <div
      className="custom-select"
      style={{ position: "relative", display: "inline-block" }}
      ref={dropdownRef}
    >
      <div
        className="select-header"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backgroundColor: isHovered ? "#EDF8FA" : "#fff",
          color: "rgba(129, 134, 134, 1)",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          cursor: "pointer",
          width: "140px",
          height: "40px",
          textAlign: "left",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 500,
          transition: "background-color 0.2s ease",
        }}
      >
        <span>{selected}</span>
        <ChevronDown />
      </div>
      {isOpen && (
        <ul
          className="select-options"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            listStyle: "none",
            margin: 0,
            padding: 0,
            backgroundColor: "white",
            border: "none",
            borderRadius: "4px",
            zIndex: 1000,
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            fontWeight: 500,
          }}
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              onClick={() => handleSelect(opt)}
              className="select-option"
              style={{
                padding: "8px 12px",
                color: "rgba(129, 134, 134, 1)",
                backgroundColor: "white",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
